import { graphql, useStaticQuery } from "gatsby";
import React, { useCallback, useMemo } from "react";
import { Path, useFormContext } from "react-hook-form";

import { ConsentsListItemApi } from "../../../../api-http/types";
import { useLocaleContext } from "../../../../i18n/locale-context";
import { downloadFileFromUrl } from "../../../../utils/download-file";
import { Cta } from "../../../buttons/cta";
import { Icon } from "../../../icon";
import { Checkbox, FormFieldStatus } from "../../elements";
import { PrivacyFormFieldsLabels, PrivacyFormFieldsTypes } from "./types";

export interface PrivacyFormFieldsProps {
  labels: PrivacyFormFieldsLabels;
  consents: ConsentsListItemApi[] | null;
  hideMarketing?: boolean;
}

export function PrivacyFormFields({ labels, consents, hideMarketing }: PrivacyFormFieldsProps) {
  const formMethods = useFormContext<PrivacyFormFieldsTypes>();

  // Consents are taken from two sources for no real reason other than
  // backend has not enough time to implement CTA field management and release draws near
  const { allContentfulConsent } = useStaticQuery<Queries.Query>(graphql`
    query {
      allContentfulConsent {
        nodes {
          ...ConsentData
        }
      }
    }
  `);
  const { locale } = useLocaleContext();

  const consentsMap: (ConsentsListItemApi & Partial<Queries.ConsentDataFragment>)[] | undefined =
    useMemo(
      () =>
        consents?.map((item) => {
          const contentfulItem: Queries.ConsentDataFragment | undefined =
            allContentfulConsent.nodes?.find(
              (consentItem) =>
                consentItem.consentId === item.consentId && consentItem.node_locale === locale
            ) as Queries.ConsentDataFragment | undefined;
          const fallbackContentfulItem: Queries.ConsentDataFragment | undefined =
            allContentfulConsent.nodes?.find(
              (consentItem) =>
                consentItem.consentId === item.consentId && consentItem.node_locale === "en"
            ) as Queries.ConsentDataFragment | undefined;
          return { ...(contentfulItem ?? fallbackContentfulItem), ...item };
        }),
      [consents, allContentfulConsent, locale]
    );

  const renderPrivacyCheckbox = useCallback(
    (consent: ConsentsListItemApi & Partial<Queries.ConsentDataFragment>) => {
      const handleDownload = () => {
        consent.policies?.forEach(
          (policy, i) => policy?.url && downloadFileFromUrl(policy.url, `${name}-${i}`, "pdf")
        );
      };
      if (!consent?.consentId) return null;
      const name = `consents.${consent.consentId}` as Path<PrivacyFormFieldsTypes>;
      return (
        <Checkbox
          {...formMethods.register(name, {
            required: consent.mandatory ? labels.requiredFieldMessage : undefined,
          })}
          required={!!consent.mandatory}
          collapseMessage
          status={FormFieldStatus.Error}
          key={name}
          disableLabelDecorations={true}
          defaultChecked={consent.consentId === "marketing" && hideMarketing}
          className={consent.consentId === "marketing" && hideMarketing ? "hidden" : ""}
        >
          <div className="flex flex-col items-start">
            <div>
              <span className="inline">{consent.value}</span>
              {/* Needs to be here instead of in the dedicated property of Checkbox, 
                because it must be near the text and not the icon */}
              {!!consent.mandatory && <span>*</span>}
              {consent.policies?.length && (
                <button
                  className="inline relative h-6 w-6 ml-1"
                  type="button"
                  disabled={!consent.policies?.length}
                  onClick={handleDownload}
                >
                  <Icon
                    icon="attachment"
                    className="text-primary w-8 h-8 absolute left-0 bottom-0"
                  />
                </button>
              )}
            </div>
            {consent.cta && (
              // Styleless Cta
              <Cta className="text-primary" size="sm" {...consent.cta}>
                {consent.cta.label}
              </Cta>
            )}
          </div>
        </Checkbox>
      );
    },
    [formMethods, labels, hideMarketing]
  );

  return consentsMap?.length ? (
    <div className="w-full flex flex-col gap-2">{consentsMap.map(renderPrivacyCheckbox)}</div>
  ) : null;
}
